import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Chat from '../components/Chat';
import NewMessageForm from '../components/NewMessageForm';
import NoMessages from '../components/NoMessages';
import Topics from '../components/Topics';
import i18nContext from 'components/i18n-context';
import { CUSTOMER_PERMISSIONS, MESSAGE_TOPIC_PERMISSIONS, BACKEND_MODULE_TYPES } from 'components/common/constants';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import Loader from 'components/common/Loader';
import { ROUTE_PATHS } from 'routes/constants';
import { useMediaQuery } from 'services/useMediaQUery';
import { MEDIA_QUERY } from 'services/utils';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { PopUp } from 'uikit/PopUp/PopUp';
import '../Messages.scss';

const MessagesDashboard = ({ userAccount, messagesStore, userStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const topicsRef = useRef(null);
  const [isAnnouncementTopic, setIsAnnouncementTopic] = useState(false);

  const clearError = () => messagesStore.clearError();
  const setIsNewTopicCreatedStatus = (status) => messagesStore.setIsNewTopicCreated(status);
  const setIsNewMessageCreated = (status) => messagesStore.setIsNewMessageCreated(status);
  const setNewTopicData = (subject, message) => messagesStore.setNewTopicData(subject, message);
  const setNewMessageData = (message) => messagesStore.setNewMessageData(message);
  const clearUploadedFiles = () => messagesStore.clearUploadedFiles();
  const removeAttachment = (fileId) => messagesStore.removeAttachment(fileId);
  const getCurrentTopicMessagesFilesUrls = async () => {
    await messagesStore.getCurrentTopicMessagesFilesUrls(userAccount?.account_number);
  };
  const downloadAttachment = async (fileId) => {
    await messagesStore.downloadAttachment(userAccount?.account_number, fileId);
  };
  const generateTopicSecurityCode = async () =>
    await messagesStore.generateNewTopicSecurityCode(userAccount?.account_number);
  const generateMessageSecurityCode = async (topicId) =>
    await messagesStore.generateNewMessageSecurityCode(userAccount?.account_number, topicId);
  const resendSecurityCode = async () => await messagesStore.resendMessageSecurityCode();
  const createNewTopic = async (securityCode) =>
    await messagesStore.confirmNewTopicCreation(userAccount?.account_number, securityCode);
  const createNewMessage = async (topicId, securityCode) => {
    await messagesStore.confirmNewMessageCreation(userAccount?.account_number, topicId, securityCode);
  };

  const handleClickNewMessageButton = () => {
    navigate(ROUTE_PATHS.MESSAGES_CREATE_NEW_TOPIC);
  };

  const handleChangeActiveTopic = async (topicId) => {
    navigate(`/messages/${topicId}`);
  };

  useEffect(() => {
    if (messagesStore.isNewTopicCreated && messagesStore.topics.length > 0) {
      handleChangeActiveTopic(messagesStore.topics[0]?.id);
      setIsNewTopicCreatedStatus(false);
    }
  }, [messagesStore.isNewTopicCreated, messagesStore.topics]);

  useEffect(() => {
    if (messagesStore.topics.length > 0 && messagesStore.currentTopicIndex !== -1) {
      if (messagesStore.topics[messagesStore.currentTopicIndex].permission === MESSAGE_TOPIC_PERMISSIONS.ANNOUNCEMENT) {
        setIsAnnouncementTopic(true);
      } else {
        setIsAnnouncementTopic(false);
      }
    }
  }, [messagesStore.topics, messagesStore.currentTopicIndex]);

  useEffect(() => {
    messagesStore.resetForms();
  }, [location.pathname]);

  const isNoMessage = messagesStore.topics.length === 0 && !messagesStore.isLoading;
  const isTopics = !isMobile || (isMobile && location.pathname === ROUTE_PATHS.MESSAGES);
  const isChat = messagesStore.currentTopicIndex !== -1;
  const isCreateNewTopicForm = location.pathname === ROUTE_PATHS.MESSAGES_CREATE_NEW_TOPIC;

  const showWriteNewMessage = userStore.hasPermissions([CUSTOMER_PERMISSIONS.CREATE_MESSAGE]);

  const handleCloseInternalActionConfirmation = () => {
    messagesStore.setIsShowingInternalActionConfirmation(false);
  };

  return (
    <NewContainer
      className={'messages-container content-card-wrapper'}
      header={i18n.getMessage('navigation.messages.text')}
      isBackButton={location.pathname !== ROUTE_PATHS.MESSAGES}
    >
      {!messagesStore.isInitialized ? (
        <div className='loader-container'>
          <Loader />
        </div>
      ) : (
        <div className='messages-wrapper'>
          {isNoMessage ? (
            <NoMessages
              handleClickNewMessageButton={handleClickNewMessageButton}
              showWriteNewMessage={showWriteNewMessage}
            />
          ) : (
            isTopics && (
              <Topics
                topics={messagesStore.topics}
                activeTopicIndex={messagesStore.currentTopicIndex}
                handleChangeActiveTopic={handleChangeActiveTopic}
                handleClickNewMessageButton={handleClickNewMessageButton}
                topicsRef={topicsRef}
                showWriteMessageButton={showWriteNewMessage}
              />
            )
          )}
          {isCreateNewTopicForm ? (
            <NewMessageForm
              generateSecurityCode={generateTopicSecurityCode}
              newTopicData={messagesStore.newTopic}
              setNewTopicData={setNewTopicData}
              isLoading={messagesStore.isLoading || messagesStore.isFileUploading}
              error={messagesStore.error}
              clearError={clearError}
              uploadedFiles={messagesStore.uploadedFiles}
              uploadDocuments={messagesStore.uploadAttachments(userAccount?.account_number)}
              removeAttachment={removeAttachment}
              isShowingInternalActionConfirmation={messagesStore.isShowingInternalActionConfirmation}
            />
          ) : (
            isChat && (
              <Chat
                topic={messagesStore.topics[messagesStore.currentTopicIndex]}
                messages={messagesStore.currentTopicMessages}
                userAccount={userAccount}
                isLoading={messagesStore.isTopicMessagesLoading || messagesStore.isFileUploading}
                error={messagesStore.error}
                clearError={clearError}
                generateSecurityCode={generateMessageSecurityCode}
                setNewMessageData={setNewMessageData}
                uploadedFiles={messagesStore.uploadedFiles}
                uploadDocuments={messagesStore.uploadAttachments(userAccount?.account_number)}
                removeAttachment={removeAttachment}
                getCurrentTopicMessagesFilesUrls={getCurrentTopicMessagesFilesUrls}
                downloadAttachment={downloadAttachment}
                topicsRef={topicsRef}
                canWriteMessages={showWriteNewMessage && !isAnnouncementTopic}
                clearUploadedFiles={clearUploadedFiles}
                isNewMessageCreated={messagesStore.isNewMessageCreated}
                setIsNewMessageCreated={setIsNewMessageCreated}
                isShowingInternalActionConfirmation={messagesStore.isShowingInternalActionConfirmation}
              />
            )
          )}
        </div>
      )}

      <PopUp
        className={'action-confirmation-popup'}
        show={messagesStore.isShowingInternalActionConfirmation}
        onClose={handleCloseInternalActionConfirmation}
        alignOnCenter={true}
      >
        <PopUpInternalActionConfirmationScheme
          isLoading={messagesStore.isLoading}
          isShowSwitchConfirmationMethodScreen={messagesStore.isShowSwitchConfirmationMethodScreen}
          title={i18n.getMessage('messages.popUp.actionConfirmation.title')}
          confirmButtonLabel={i18n.getMessage('messages.popUp.actionConfirmation.button.confirm')}
          onClose={handleCloseInternalActionConfirmation}
          onConfirm={async (securityCode) => {
            if (location.pathname === ROUTE_PATHS.MESSAGES_CREATE_NEW_TOPIC) {
              await createNewTopic(securityCode);
            } else {
              await createNewMessage(messagesStore.topics[messagesStore.currentTopicIndex]?.id, securityCode);
            }
          }}
          clearError={() => messagesStore.clearError()}
          error={messagesStore.error}
          resendSecurityCode={resendSecurityCode}
          setIsShowSwitchConfirmationMethodScreen={(status) =>
            messagesStore.setIsShowSwitchConfirmationMethodScreen(status)
          }
          handleForceSwitchToAnotherConfirmationType={messagesStore.forceSwitchToAnotherConfirmationType}
          confirmationId={messagesStore.confirmationId}
          module={BACKEND_MODULE_TYPES.MESSENGER}
        />
      </PopUp>
    </NewContainer>
  );
};

MessagesDashboard.propTypes = {
  userAccount: MobXPropTypes.observableObject,
  messagesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userStore: stores.userStore,
  userAccount: stores.userStore.userData.account,
  messagesStore: stores.messagesStore
}))(observer(MessagesDashboard));
